import { FocusEvent, useEffect, useMemo, useRef, useState } from "react";

export type DismissTimerBindings = {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: (event: FocusEvent<HTMLElement>) => void;
};

export type DismissTimer = {
  /** True while the clock is stopped: hovered, focused within, or the window is blurred. */
  paused: boolean;
  /** Spread onto the toast root so hover and focus reach the clock. */
  bindings: DismissTimerBindings;
};

/**
 * The kit-owned dismiss clock. Notistack's own timer restarts at half the
 * duration after a hover, which a visible countdown cannot follow, so timed
 * toasts are enqueued with no notistack timer and this hook closes them: it
 * pauses on hover, on keyboard focus within the toast and on window blur, and
 * resumes from the remaining time. A `null` duration never expires. The
 * duration is fixed for the life of the toast; changing it is not supported.
 */
export function useDismissTimer(duration: number | null, onExpire: () => void): DismissTimer {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [windowBlurred, setWindowBlurred] = useState(false);
  const paused = hovered || focused || windowBlurred;

  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  });

  // Time left when the clock last stopped. Only meaningful for a numeric duration.
  const remainingRef = useRef(duration ?? 0);

  useEffect(() => {
    const onBlur = () => setWindowBlurred(true);
    const onFocus = () => setWindowBlurred(false);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    if (duration === null || paused) return undefined;
    const startedAt = Date.now();
    const timer = setTimeout(() => onExpireRef.current(), remainingRef.current);
    return () => {
      clearTimeout(timer);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [duration, paused]);

  const bindings = useMemo<DismissTimerBindings>(
    () => ({
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
      onFocus: () => setFocused(true),
      onBlur: (event) => {
        // Focus moving between the toast's own controls is not a pause boundary.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocused(false);
        }
      },
    }),
    [],
  );

  return { paused, bindings };
}
